import { useState } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Sun, 
  Coffee, 
  Sunset, 
  Moon,
  MessageCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MapPin,
  Share2,
  Check,
  Loader2
} from 'lucide-react';
import { useLocation } from '../context/LocationContext';
import { useLanguage } from '../context/LanguageContext';
import { API } from '../lib/api';

// Day plans by location and guest type
const DAY_PLANS = {
  monopoli: {
    couple: {
      morning: {
        activity: { en: 'Cala Porta Vecchia', it: 'Cala Porta Vecchia' },
        description: { 
          en: 'Start with a morning swim at this iconic cove — arrive by 9am for the best light',
          it: 'Inizia con una nuotata mattutina in questa cala iconica — arriva entro le 9 per la luce migliore'
        },
        time: '09:00',
        type: 'beach'
      },
      lunch: {
        activity: { en: 'La Locanda dei Pescatori', it: 'La Locanda dei Pescatori' },
        description: {
          en: 'Fresh seafood on the harbor — try the crudo platter and local white wine',
          it: 'Pesce fresco sul porto — prova il piatto di crudo e vino bianco locale'
        },
        time: '13:00',
        type: 'restaurant',
        price: '€35-50'
      },
      afternoon: {
        activity: { en: 'Private Boat Tour', it: 'Tour in Barca Privato' },
        description: {
          en: '3-hour exploration of hidden caves and swimming spots — Prosecco included',
          it: 'Esplorazione di 3 ore di grotte nascoste e posti per nuotare — Prosecco incluso'
        },
        time: '16:00',
        type: 'experience',
        price: '€180'
      },
      dinner: {
        activity: { en: 'Carlo Quinto', it: 'Carlo Quinto' },
        description: {
          en: 'Romantic rooftop dinner in the old town — book the sunset table',
          it: 'Cena romantica sul tetto nel centro storico — prenota il tavolo al tramonto'
        },
        time: '20:30',
        type: 'restaurant',
        price: '€60-90'
      }
    },
    family: {
      morning: {
        activity: { en: 'Porto Ghiacciolo', it: 'Porto Ghiacciolo' },
        description: {
          en: 'Calm, shallow waters perfect for kids — easy parking nearby',
          it: 'Acque calme e basse perfette per i bambini — parcheggio facile nelle vicinanze'
        },
        time: '09:30',
        type: 'beach'
      },
      lunch: {
        activity: { en: 'Radici', it: 'Radici' },
        description: {
          en: 'Kid-friendly trattoria with amazing orecchiette — outdoor garden seating',
          it: 'Trattoria adatta ai bambini con orecchiette fantastiche — posti a sedere nel giardino'
        },
        time: '12:30',
        type: 'restaurant',
        price: '€25-40'
      },
      afternoon: {
        activity: { en: 'Old Town Walking Tour', it: 'Tour a Piedi nel Centro Storico' },
        description: {
          en: 'Explore the white-washed streets and stop for gelato at Martinucci',
          it: 'Esplora le strade imbiancate e fermati per un gelato da Martinucci'
        },
        time: '16:00',
        type: 'experience'
      },
      dinner: {
        activity: { en: 'Pizzeria Da Sergio', it: 'Pizzeria Da Sergio' },
        description: {
          en: 'Best pizza in town — relaxed vibe, kids love it',
          it: 'La migliore pizza in città — atmosfera rilassata, i bambini la adorano'
        },
        time: '19:30',
        type: 'restaurant',
        price: '€20-30'
      }
    },
    luxury: {
      morning: {
        activity: { en: 'Lido Santo Stefano', it: 'Lido Santo Stefano' },
        description: {
          en: 'Reserved cabana with full service — champagne breakfast available',
          it: 'Cabana riservata con servizio completo — colazione con champagne disponibile'
        },
        time: '10:00',
        type: 'beach-club',
        price: '€80-120'
      },
      lunch: {
        activity: { en: 'Grotta Palazzese', it: 'Grotta Palazzese' },
        description: {
          en: 'Lunch inside a natural cave overlooking the sea — bucket list dining',
          it: 'Pranzo dentro una grotta naturale con vista sul mare — esperienza da non perdere'
        },
        time: '13:00',
        type: 'restaurant',
        price: '€100-150'
      },
      afternoon: {
        activity: { en: 'Private Yacht Charter', it: 'Noleggio Yacht Privato' },
        description: {
          en: 'Half-day on a luxury yacht with captain and chef — swim, sun, sip',
          it: 'Mezza giornata su uno yacht di lusso con capitano e chef — nuota, prendi il sole, sorseggia'
        },
        time: '16:00',
        type: 'experience',
        price: '€500+'
      },
      dinner: {
        activity: { en: 'Private Chef at Your Villa', it: 'Chef Privato nella Tua Villa' },
        description: {
          en: 'Michelin-trained chef prepares a 7-course tasting menu at your accommodation',
          it: 'Chef con formazione Michelin prepara un menu degustazione di 7 portate nel tuo alloggio'
        },
        time: '20:00',
        type: 'experience',
        price: '€200/person'
      }
    },
    budget: {
      morning: {
        activity: { en: 'Porto Bianco Beach', it: 'Spiaggia di Porto Bianco' },
        description: {
          en: 'Free public beach with stunning views — bring your own umbrella',
          it: 'Spiaggia pubblica gratuita con viste mozzafiato — porta il tuo ombrellone'
        },
        time: '09:00',
        type: 'beach'
      },
      lunch: {
        activity: { en: 'Puccia from Panificio Antico Forno', it: 'Puccia dal Panificio Antico Forno' },
        description: {
          en: 'Local stuffed bread — the best €5 lunch you\'ll ever have',
          it: 'Pane farcito locale — il miglior pranzo da €5 che avrai mai'
        },
        time: '13:00',
        type: 'street-food',
        price: '€5-8'
      },
      afternoon: {
        activity: { en: 'Free Walking Tour', it: 'Tour a Piedi Gratuito' },
        description: {
          en: 'Explore the old town with a local guide — tip-based',
          it: 'Esplora il centro storico con una guida locale — a offerta libera'
        },
        time: '17:00',
        type: 'experience'
      },
      dinner: {
        activity: { en: 'Aperitivo at Bar Palazzo', it: 'Aperitivo al Bar Palazzo' },
        description: {
          en: 'Spritz with free buffet — locals\' secret for a cheap dinner',
          it: 'Spritz con buffet gratuito — segreto dei locali per una cena economica'
        },
        time: '19:00',
        type: 'bar',
        price: '€8-12'
      }
    }
  },
  fasano: {
    couple: {
      morning: {
        activity: { en: 'Borgo Egnazia Beach Club', it: 'Beach Club Borgo Egnazia' },
        description: {
          en: 'Start the day in pure luxury — private cabana overlooking pristine waters',
          it: 'Inizia la giornata nel puro lusso — cabana privata con vista su acque incontaminate'
        },
        time: '10:00',
        type: 'beach-club',
        price: '€150+'
      },
      lunch: {
        activity: { en: 'Il Frantoio', it: 'Il Frantoio' },
        description: {
          en: 'Farm-to-table in a historic olive mill — their burrata is legendary',
          it: 'Dal campo alla tavola in un frantoio storico — la loro burrata è leggendaria'
        },
        time: '13:00',
        type: 'restaurant',
        price: '€50-70'
      },
      afternoon: {
        activity: { en: 'Cooking Class at a Masseria', it: 'Corso di Cucina in una Masseria' },
        description: {
          en: 'Learn to make orecchiette and tiramisu with a local nonna',
          it: 'Impara a fare orecchiette e tiramisù con una nonna locale'
        },
        time: '16:00',
        type: 'experience',
        price: '€120/person'
      },
      dinner: {
        activity: { en: 'Due Camini', it: 'Due Camini' },
        description: {
          en: 'Two Michelin stars — book the tasting menu for the full experience',
          it: 'Due stelle Michelin — prenota il menu degustazione per l\'esperienza completa'
        },
        time: '20:00',
        type: 'restaurant',
        price: '€150-200'
      }
    },
    family: {
      morning: {
        activity: { en: 'Torre Canne Beach', it: 'Spiaggia di Torre Canne' },
        description: {
          en: 'Long sandy beach with gentle waves — perfect for little ones',
          it: 'Lunga spiaggia sabbiosa con onde dolci — perfetta per i piccoli'
        },
        time: '09:30',
        type: 'beach'
      },
      lunch: {
        activity: { en: 'Masseria Torre Coccaro', it: 'Masseria Torre Coccaro' },
        description: {
          en: 'Family-friendly luxury with a beautiful garden — kids menu available',
          it: 'Lusso adatto alle famiglie con un bellissimo giardino — menu bambini disponibile'
        },
        time: '12:30',
        type: 'restaurant',
        price: '€40-60'
      },
      afternoon: {
        activity: { en: 'Zoo Safari Fasano', it: 'Zoo Safari Fasano' },
        description: {
          en: 'Drive-through safari with lions, giraffes and more — kids love it',
          it: 'Safari in auto con leoni, giraffe e altro — i bambini lo adorano'
        },
        time: '15:00',
        type: 'experience',
        price: '€25/person'
      },
      dinner: {
        activity: { en: 'Masseria Salamina', it: 'Masseria Salamina' },
        description: {
          en: 'Traditional Puglian dinner with outdoor seating and local wine',
          it: 'Cena tradizionale pugliese con posti a sedere all\'aperto e vino locale'
        },
        time: '19:30',
        type: 'restaurant',
        price: '€35-50'
      }
    },
    luxury: {
      morning: {
        activity: { en: 'Spa at Borgo Egnazia', it: 'Spa al Borgo Egnazia' },
        description: {
          en: 'Start with the signature olive oil ritual — pure Mediterranean wellness',
          it: 'Inizia con il rituale dell\'olio d\'oliva — puro benessere mediterraneo'
        },
        time: '09:00',
        type: 'spa',
        price: '€200+'
      },
      lunch: {
        activity: { en: 'Cibus at Borgo Egnazia', it: 'Cibus al Borgo Egnazia' },
        description: {
          en: 'Contemporary Puglian cuisine with panoramic views',
          it: 'Cucina pugliese contemporanea con vista panoramica'
        },
        time: '13:00',
        type: 'restaurant',
        price: '€80-100'
      },
      afternoon: {
        activity: { en: 'Private Wine Tasting', it: 'Degustazione di Vini Privata' },
        description: {
          en: 'Helicopter to a prestigious vineyard — taste Primitivo at the source',
          it: 'Elicottero verso un prestigioso vigneto — degusta il Primitivo alla fonte'
        },
        time: '16:00',
        type: 'experience',
        price: '€400/couple'
      },
      dinner: {
        activity: { en: 'Private Chef at Your Masseria', it: 'Chef Privato nella Tua Masseria' },
        description: {
          en: 'A culinary journey through Puglia, served under the stars',
          it: 'Un viaggio culinario attraverso la Puglia, servito sotto le stelle'
        },
        time: '20:00',
        type: 'experience',
        price: '€250/person'
      }
    },
    budget: {
      morning: {
        activity: { en: 'Savelletri Beach', it: 'Spiaggia di Savelletri' },
        description: {
          en: 'Free beach near the fishing village — authentic local vibe',
          it: 'Spiaggia libera vicino al villaggio di pescatori — atmosfera locale autentica'
        },
        time: '09:00',
        type: 'beach'
      },
      lunch: {
        activity: { en: 'Panzerotti at the Market', it: 'Panzerotti al Mercato' },
        description: {
          en: 'Deep-fried pockets of heaven — the best €3 you\'ll spend',
          it: 'Tasche fritte di paradiso — i migliori €3 che spenderai'
        },
        time: '12:30',
        type: 'street-food',
        price: '€3-5'
      },
      afternoon: {
        activity: { en: 'Explore Egnazia Ruins', it: 'Esplora le Rovine di Egnazia' },
        description: {
          en: 'Ancient Roman and Messapian ruins — stunning history for just €6',
          it: 'Antiche rovine romane e messapiche — storia straordinaria per soli €6'
        },
        time: '16:00',
        type: 'culture',
        price: '€6'
      },
      dinner: {
        activity: { en: 'Trattoria La Cantina', it: 'Trattoria La Cantina' },
        description: {
          en: 'No-frills local cooking — massive portions, tiny prices',
          it: 'Cucina locale senza fronzoli — porzioni enormi, prezzi piccoli'
        },
        time: '19:30',
        type: 'restaurant',
        price: '€15-25'
      }
    }
  }
};

const getIcon = (slot) => {
  const icons = {
    morning: Sun,
    lunch: Coffee,
    afternoon: Sunset,
    dinner: Moon
  };
  return icons[slot] || Sun;
};

export default function DayPlanner() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSharing, setIsSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const { location, guestType, guestTypeData, locationData } = useLocation();
  const { language } = useLanguage();

  const translations = {
    en: {
      title: "Your Perfect Day",
      subtitle: "Curated just for you",
      planFor: "Plan for",
      in: "in",
      refresh: "Show alternative",
      contact: "Contact on WhatsApp",
      showPlan: "Plan my day",
      hidePlan: "Hide day plan",
      shareDay: "Share My Day",
      sharing: "Creating share link...",
      shared: "Link copied!"
    },
    it: {
      title: "La Tua Giornata Perfetta",
      subtitle: "Curata apposta per te",
      planFor: "Piano per",
      in: "a",
      refresh: "Mostra alternativa",
      contact: "Contatta su WhatsApp",
      showPlan: "Pianifica la mia giornata",
      hidePlan: "Nascondi piano giornaliero",
      shareDay: "Condividi la Mia Giornata",
      sharing: "Creazione link...",
      shared: "Link copiato!"
    },
    fr: {
      title: "Votre Journée Parfaite",
      subtitle: "Organisée juste pour vous",
      planFor: "Plan pour",
      in: "à",
      refresh: "Montrer alternative",
      contact: "Contacter sur WhatsApp",
      showPlan: "Planifier ma journée",
      hidePlan: "Masquer le plan",
      shareDay: "Partager Ma Journée",
      sharing: "Création du lien...",
      shared: "Lien copié!"
    },
    de: {
      title: "Ihr Perfekter Tag",
      subtitle: "Speziell für Sie kuratiert",
      planFor: "Plan für",
      in: "in",
      refresh: "Alternative zeigen",
      contact: "Auf WhatsApp kontaktieren",
      showPlan: "Meinen Tag planen",
      hidePlan: "Tagesplan ausblenden",
      shareDay: "Meinen Tag teilen",
      sharing: "Link wird erstellt...",
      shared: "Link kopiert!"
    }
  };

  const slotLabels = {
    en: { morning: 'Morning', lunch: 'Lunch', afternoon: 'Afternoon', dinner: 'Dinner' },
    it: { morning: 'Mattina', lunch: 'Pranzo', afternoon: 'Pomeriggio', dinner: 'Cena' },
    fr: { morning: 'Matin', lunch: 'Déjeuner', afternoon: 'Après-midi', dinner: 'Dîner' },
    de: { morning: 'Morgen', lunch: 'Mittagessen', afternoon: 'Nachmittag', dinner: 'Abendessen' }
  };

  const txt = translations[language] || translations.en;
  const slots = slotLabels[language] || slotLabels.en;

  if (!location || !guestType) return null;

  const dayPlan = DAY_PLANS[location]?.[guestType];
  if (!dayPlan) return null;

  const whatsappNumber = locationData?.whatsapp || '393331234567';

  const handleContactAll = () => {
    const activities = Object.values(dayPlan)
      .map(item => item.activity[language] || item.activity.en)
      .join(', ');
    const message = encodeURIComponent(
      `Hi! I'd like to book this day plan: ${activities}. Can you help me arrange everything?`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const handleShareDay = async () => {
    setIsSharing(true);
    try {
      const response = await axios.post(`${API}/share/itinerary`, {
        location: location,
        guest_type: guestType,
        itinerary: dayPlan
      });
      
      const shareUrl = response.data.share_url;
      
      // Try native share first
      if (navigator.share) {
        await navigator.share({
          title: `My Perfect Day in ${locationData?.name || 'Puglia'}`,
          text: `Check out my curated day itinerary!`,
          url: shareUrl
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareUrl);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      }
    } catch (error) {
      console.error('Share error:', error);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <section className="px-6 py-6" data-testid="day-planner">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full bg-gradient-to-r from-stone-900 to-stone-800 rounded-2xl p-5 text-left hover:from-stone-800 hover:to-stone-700 transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
                <Calendar size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{txt.title}</h2>
                <p className="text-stone-400 text-sm">
                  {txt.planFor} {guestTypeData?.label[language] || guestTypeData?.label.en} {txt.in} {locationData?.name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronUp className="text-stone-400" size={20} />
              ) : (
                <ChevronDown className="text-stone-400" size={20} />
              )}
            </div>
          </div>
        </button>

        {isExpanded && (
          <div className="mt-4 bg-white rounded-2xl border border-stone-100 overflow-hidden">
            <div className="divide-y divide-stone-100">
              {['morning', 'lunch', 'afternoon', 'dinner'].map((slot) => {
                const item = dayPlan[slot];
                const Icon = getIcon(slot);
                return (
                  <div key={slot} className="p-4 flex gap-4">
                    <div className="w-16 text-center flex-shrink-0">
                      <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center ${
                        slot === 'morning' ? 'bg-amber-100 text-amber-600' :
                        slot === 'lunch' ? 'bg-orange-100 text-orange-600' :
                        slot === 'afternoon' ? 'bg-rose-100 text-rose-600' :
                        'bg-indigo-100 text-indigo-600'
                      }`}>
                        <Icon size={18} />
                      </div>
                      <p className="text-xs text-stone-500 mt-1">{item.time}</p>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-stone-400 uppercase tracking-wide font-medium">{slots[slot]}</p>
                      <h3 className="font-semibold text-stone-900 mt-0.5">
                        {item.activity[language] || item.activity.en}
                      </h3>
                      <p className="text-sm text-stone-600 mt-1">
                        {item.description[language] || item.description.en}
                      </p>
                      {item.price && (
                        <p className="text-sm text-teal-700 font-medium mt-1">{item.price}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 bg-stone-50 border-t border-stone-100 space-y-3">
              <button
                onClick={handleContactAll}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={18} />
                {txt.contact}
              </button>
              
              <button
                onClick={handleShareDay}
                disabled={isSharing}
                data-testid="share-day-btn"
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 disabled:from-violet-400 disabled:to-purple-400 text-white py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              >
                {isSharing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    {txt.sharing}
                  </>
                ) : shareSuccess ? (
                  <>
                    <Check size={18} />
                    {txt.shared}
                  </>
                ) : (
                  <>
                    <Share2 size={18} />
                    {txt.shareDay}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
